import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { DriverWorkCycleEdit } from "@/models/driverWorkShiftsAndCycle";
import { DriverWorkShift } from "@/models/operationPlans";
import { uuidv4 } from "@/util";
import { createDriverWorkCycleAPI } from "@/util/api/driverWorkCycles";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "@/components/hoc/SnackbarAlert";
import { Messages } from "@/util/Messages";
import { getDriverWorkShiftsAPI } from "@/util/api/driverWorkShifts";
import { DriverWorkCycleEditTemplate } from "@/components/driverWorkCycles/DriverWorkCycleEditTemplate";
import { DriverWorkCyclesConstants } from "./DriverWorkCyclesConstants";
import { getOfficeAPI } from "@/util/api/API";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const DriverWorkCycleCreate = () => {
  const params = useParams<{ office_id: string }>();
  const officeId = params.office_id;

  const { navigate } = useStatefulNavigator();
  const goToDetail = useCallback(() => {
    navigate(`/driver_work_cycles/${officeId}`);
  }, [navigate, officeId]);

  const [driverWorkCycle, setDriverWorkCycle] = useState<DriverWorkCycleEdit>({
    driverWorkShifts: [
      {
        number: 1,
        key: uuidv4(),
        shift: null,
      },
    ],
  });
  const [driverWorkShifts, setDriverWorkShifts] = useState<DriverWorkShift[]>(
    []
  );

  const [loading, createDriverWorkCycleAPIWrapper] = useLoadingPromise(
    createDriverWorkCycleAPI
  );

  const handleCreate = useCallback(() => {
    if (
      officeId == null ||
      !driverWorkCycle.driverWorkShifts?.length ||
      loading
    ) {
      return;
    }

    const requestShifts = driverWorkCycle.driverWorkShifts
      .map(({ shift }) => shift)
      .filter((shift) => shift != null) as DriverWorkShift[];

    createDriverWorkCycleAPIWrapper({
      ...driverWorkCycle,
      officeId: Number(officeId),
      driverWorkShifts: requestShifts,
    })
      .then(goToDetail)
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Register}${Messages.ErrorOccurred}: ${
              e?.response?.data?.message ?? ""
            }`
          );
        }
      });
  }, [
    createDriverWorkCycleAPIWrapper,
    driverWorkCycle,
    goToDetail,
    loading,
    officeId,
  ]);

  useEffect(() => {
    if (officeId == null) {
      return;
    }

    getOfficeAPI(Number(officeId))
      .then((res) => {
        setDriverWorkCycle({
          ...driverWorkCycle,
          officeId: Number(officeId),
          offices: res.data,
        });
      })
      .catch((e) =>
        console.log("===Error occurred while getting Office===", e)
      );

    getDriverWorkShiftsAPI(Number(officeId))
      .then((res) => setDriverWorkShifts(res.data))
      .catch((e) =>
        console.log("===Error occurred while getting Shifts===", e)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId]);

  return (
    <DriverWorkCycleEditTemplate
      title={DriverWorkCyclesConstants.DriverWorkCycleCreate}
      driverWorkCycle={driverWorkCycle}
      driverWorkShifts={driverWorkShifts}
      loading={loading}
      onCancel={goToDetail}
      onSubmit={handleCreate}
      onChangeData={setDriverWorkCycle}
    />
  );
};
